"use client";

import { useState, type PropsWithChildren } from "react";

import Plus from "@/assets/svg/32/Plus";
import type { ProductCardType } from "@/types/product";

import ProductCard from "@/components/molecules/ProductCard";
import {
  autoPlacement,
  offset,
  safePolygon,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions
} from "@floating-ui/react";
import clsx from "clsx";
import styles from "./tagged-image.module.css";

const ClientTag = ({
  product
}: PropsWithChildren<{ product: ProductCardType }>) => {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "absolute",
    middleware: [
      offset(5),
      shift(),
      autoPlacement((state) => {
        return {
          rootBoundary: "document",
          elementContext: "reference",
          padding: {
            top: state.rects.floating.height / 2,
            bottom: state.rects.floating.height / 2
          },

          allowedPlacements: ["bottom", "top"]
        };
      })
    ]
  });

  const focus = useFocus(context);

  const hover = useHover(context, {
    handleClose: safePolygon()
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus
  ]);

  return (
    <>
      <button
        ref={refs.setReference}
        data-product={product?.core?.id}
        className={clsx(styles.tag__client, open && styles.active)}
        {...getReferenceProps()}
      >
        <Plus />
      </button>
      {open && (
        <div
          className={styles.tagCardContent}
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps({ tabIndex: 1 })}
        >
          <ProductCard productCard={product} />
        </div>
      )}
    </>
  );
};

export default ClientTag;
